import React, { lazy, ComponentType } from 'react';
import { logger } from './logger';

/**
 * Configuration options for safeLazy loader
 */
interface SafeLazyOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number;
  /** Base delay between retries in ms (will use exponential backoff) */
  retryDelay?: number;
  /** Display name for the lazy component (for debugging) */
  displayName?: string;
}

/**
 * Creates a lazy-loaded component with built-in retry logic and error handling.
 *
 * This utility wraps React.lazy to:
 * 1. Retry failed dynamic imports with exponential backoff
 * 2. Provide better error messages for debugging
 * 3. Add displayName for React DevTools
 * 4. Handle both default and named exports
 *
 * @example
 * // Default export
 * const Dashboard = safeLazy(() => import('@/pages/Dashboard'));
 *
 * // Named export
 * const Dashboard = safeLazy(
 *   () => import('@/pages/Dashboard'),
 *   { displayName: 'Dashboard' },
 *   'Dashboard'
 * );
 *
 * @param importFn - Dynamic import function
 * @param options - Configuration options
 * @param exportName - Name of the export if not using default
 */
export function safeLazy<T extends ComponentType<any>>(
  importFn: () => Promise<{ default: T } | { [key: string]: T }>,
  options: SafeLazyOptions = {},
  exportName?: string
): React.LazyExoticComponent<T> {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    displayName,
  } = options;

  const lazyComponent = lazy(async () => {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const module = await importFn();

        // Handle named export
        if (exportName && exportName in module) {
          return { default: (module as Record<string, T>)[exportName] };
        }

        // Handle default export
        if ('default' in module) {
          return module as { default: T };
        }

        throw new Error(`Module does not have a default export or named export '${exportName}'`);
      } catch (error) {
        lastError = error as Error;

        // Don't retry on module resolution errors (syntax errors, etc.)
        if (error instanceof SyntaxError) {
          logger.error(`Module syntax error (not retrying):`, error);
          break;
        }

        // Log retry attempt
        if (attempt < maxRetries - 1) {
          const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
          logger.warn(
            `Failed to load module (attempt ${attempt + 1}/${maxRetries}), ` +
            `retrying in ${delay}ms...`,
            { error: lastError.message }
          );

          // Wait before retry with exponential backoff
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    logger.error(
      `Failed to load module after ${maxRetries} attempts`,
      { error: lastError?.message, displayName }
    );

    throw lastError || new Error('Failed to load module');
  });

  // Add displayName for React DevTools
  if (displayName) {
    (lazyComponent as any).displayName = `Lazy(${displayName})`;
  }

  return lazyComponent;
}

/**
 * Preload a lazy component without rendering it.
 * Useful for prefetching on hover or route change.
 *
 * @param importFn - The same import function used for safeLazy
 */
export function preloadComponent(
  importFn: () => Promise<any>
): Promise<void> {
  return importFn()
    .then(() => {
      logger.debug('Component preloaded successfully');
    })
    .catch((error) => {
      logger.warn('Failed to preload component', { error: error.message });
    });
}

export default safeLazy;
